import { useEffect, useState } from "react";
import { supabase } from "./supabase";
import type { Resposta } from "./adminCatalogo";

/**
 * As PESQUISAS de satisfação (NPS) e os INDICADORES do produto (000310-000312 —
 * o G1 do Plano de Melhorias da Equipe de Produtos, e a decisão 2 de Victor).
 *
 * ZERO regra aqui. Quem pode responder (o aluno com o curso vigente), quem pode
 * ser avaliado (os professores ATIVOS da grade da turma dele), se a pesquisa
 * está aberta, e a CONTA do NPS são do banco — num lugar só, `nps_calculo`. A
 * tela não recalcula nem arredonda: número que ela mostra é número que veio.
 */

// ── O aluno ─────────────────────────────────────────────────────────────────

export type MinhaResposta = { nota: number; comentario: string | null; atualizada_em: string };

export type PesquisaDoAluno = {
  id: string;
  curso_slug: string;
  curso_nome: string;
  alvo: "curso" | "professor";
  titulo: string;
  pergunta: string;
  fecha_em: string | null;
  /** A resposta dele à pesquisa de CURSO; nula se ainda não respondeu. */
  resposta: MinhaResposta | null;
  /** Na pesquisa de PROFESSOR, os docentes da turma dele — vazio sem matrícula. */
  docentes: Array<{ id: string; nome: string; resposta: MinhaResposta | null }> | null;
};

/** As pesquisas ABERTAS dos cursos do aluno. Falha de leitura vira `null`. */
export async function lerMinhasPesquisas(): Promise<PesquisaDoAluno[] | null> {
  const { data, error } = await supabase.rpc("minhas_pesquisas");
  if (error || !Array.isArray(data)) return null;
  return data as PesquisaDoAluno[];
}

/**
 * As pesquisas abertas do aluno, para o AVISO na página do curso. Vazio enquanto
 * carrega ou se falhar: o aviso é um convite, e a página do curso não pode
 * deixar de abrir por causa dele (o mesmo desenho de `useNovidades`).
 */
export function useMinhasPesquisas(): PesquisaDoAluno[] {
  const [pesquisas, setPesquisas] = useState<PesquisaDoAluno[]>([]);
  useEffect(() => {
    let vivo = true;
    void lerMinhasPesquisas().then((p) => {
      if (vivo) setPesquisas(p ?? []);
    });
    return () => {
      vivo = false;
    };
  }, []);
  return pesquisas;
}

/**
 * Grava (ou MUDA) a resposta. Mandar de novo com outra nota é mudar de ideia, e
 * a pesquisa guarda a última — o banco diz qual das duas aconteceu na frase.
 */
export async function responderPesquisa(
  pesquisa: string,
  docente: string | null,
  nota: number,
  comentario: string | null,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("responder_pesquisa", {
    p_pesquisa: pesquisa,
    p_docente: docente,
    p_nota: nota,
    p_comentario: comentario,
  });
  if (error) return { ok: false, detalhe: "não conseguimos enviar agora — confira a conexão e tente de novo." };
  return data as Resposta;
}

// ── O link do e-mail (000320) ───────────────────────────────────────────────
// O aluno chega sem conta (4 perfis de 67 mil têm uma, medido em 28/09): quem
// faz o papel da autenticação é o TOKEN do convite, e as quatro funções abaixo
// são as únicas das pesquisas que `anon` alcança. Nenhuma devolve o nome ou o
// e-mail do aluno — o link encaminhado mostraria a quem o recebeu só a pesquisa.

const FORMA_DO_TOKEN = /^[0-9a-f]{32}$/;
const LINK_INVALIDO = "este link de pesquisa não vale — confira se ele chegou inteiro no e-mail.";

export type EstadoDoConvite =
  | { ok: true; pesquisa: PesquisaDoAluno; descadastrado: boolean }
  | { ok: false; motivo: string; detalhe: string; curso_nome?: string };

/** A pesquisa do convite. Falha de CONEXÃO vira `null` — é outra frase que "link inválido". */
export async function lerPesquisaPorConvite(token: string): Promise<EstadoDoConvite | null> {
  // Token fora da forma nem vai ao banco: a resposta seria a mesma, e é uma
  // ida a menos para quem varre.
  if (!FORMA_DO_TOKEN.test(token)) return { ok: false, motivo: "link_invalido", detalhe: LINK_INVALIDO };
  const { data, error } = await supabase.rpc("pesquisa_por_convite", { p_token: token });
  if (error || !data) return null;
  return data as EstadoDoConvite;
}

export async function responderPorConvite(
  token: string,
  docente: string | null,
  nota: number,
  comentario: string | null,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("responder_pesquisa_por_convite", {
    p_token: token,
    p_docente: docente,
    p_nota: nota,
    p_comentario: comentario,
  });
  if (error) return { ok: false, detalhe: "não conseguimos enviar agora — confira a conexão e tente de novo." };
  return data as Resposta;
}

export async function descadastrarPorConvite(token: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("descadastrar_pesquisas_por_convite", { p_token: token });
  if (error) return { ok: false, detalhe: "não conseguimos registrar agora — confira a conexão e tente de novo." };
  return data as Resposta;
}

export async function voltarAReceberPorConvite(token: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("voltar_a_receber_pesquisas_por_convite", { p_token: token });
  if (error) return { ok: false, detalhe: "não conseguimos registrar agora — confira a conexão e tente de novo." };
  return data as Resposta;
}

/**
 * Quantas respostas FALTAM numa pesquisa: a de curso é uma; a de professor, uma
 * por docente da turma. Apresentação do que veio do banco, não regra de acesso.
 */
export function faltamResponder(p: PesquisaDoAluno): number {
  if (p.alvo === "curso") return p.resposta ? 0 : 1;
  return (p.docentes ?? []).filter((d) => !d.resposta).length;
}

// ── A equipe ────────────────────────────────────────────────────────────────
//
// Desde a 000332 as pesquisas moram na aba FEEDBACKS (a chave `avaliacoes` da
// matriz), ao lado das avaliações das ferramentas, e as funções delas perguntam
// por essa aba. Os nomes `cat_admin_*` ficaram: são do tempo em que as pesquisas
// moravam na página do produto, e o front no ar os chamava.

/** Um curso na lista da seção de pesquisas (000332). As contas são do banco. */
export type CursoDasPesquisas = {
  slug: string;
  nome: string;
  /** Curso de pós, MBA ou formação: é o que oferece a pesquisa sobre os professores. */
  faculdade: boolean;
  pesquisas: number;
  abertas: number;
  respostas: number;
  /** Quando a pesquisa mais nova do curso foi aberta; nulo sem pesquisa. */
  mais_nova_em: string | null;
};

export async function lerCursosDasPesquisas(): Promise<{ ok: true; cursos: CursoDasPesquisas[] } | (Resposta & { ok: false })> {
  const { data, error } = await supabase.rpc("nps_admin_cursos");
  if (error) return { ok: false, detalhe: error.message };
  return data as { ok: true; cursos: CursoDasPesquisas[] } | (Resposta & { ok: false });
}

export type PesquisaDoProduto = {
  id: string;
  alvo: "curso" | "professor";
  titulo: string;
  pergunta: string;
  abre_em: string;
  fecha_em: string | null;
  fechada_em: string | null;
  fechada_por: string | null;
  aberta: boolean;
  criada_por: string;
  criada_em: string;
  respostas: number;
  promotores: number;
  neutros: number;
  detratores: number;
  /** Nulo sem resposta — e nulo não é zero. */
  nps: number | null;
  /** Quantas respostas deram cada nota, de 0 a 10 (onze posições). */
  por_nota: number[];
  /** Os 50 mais novos, SEM quem escreveu. O total está em `com_comentario`. */
  comentarios: Array<{ nota: number; comentario: string; professor: string | null; em: string }>;
  com_comentario: number;
  por_professor: Array<{ docente_id: string; nome: string; respostas: number; nps: number | null }> | null;
  /**
   * Na pesquisa de PROFESSOR, o que já está na Central de Professores (000318,
   * o C7): lá cada professor vê a nota e o comentário que recebeu, sem o nome
   * de quem respondeu. Nulo na pesquisa de curso, que não vai.
   */
  central: { levadas: number; faltam: number; com_erro: number; ultimo_erro: string | null } | null;
  /**
   * O convite por e-mail (000320): quantos entraram na fila, quantos saíram,
   * quantos falharam (três vezes o endereço recusado), quantos foram dispensados
   * na hora de mandar — e por quê — e quantos dos convidados responderam.
   */
  convites: {
    total: number;
    enviados: number;
    na_fila: number;
    falharam: number;
    dispensados: number;
    responderam: number;
    ultimo_envio_em: string | null;
    ultimo_erro: string | null;
    dispensados_por: Record<string, number>;
  };
};

export async function lerPesquisasDoProduto(
  curso: string,
): Promise<{ ok: true; pesquisas: PesquisaDoProduto[] } | (Resposta & { ok: false })> {
  const { data, error } = await supabase.rpc("cat_admin_pesquisas_do_produto", { p_curso_slug: curso });
  if (error) return { ok: false, detalhe: error.message };
  return data as { ok: true; pesquisas: PesquisaDoProduto[] } | (Resposta & { ok: false });
}

export async function criarPesquisa(campos: {
  curso: string;
  alvo: "curso" | "professor";
  titulo: string;
  pergunta: string;
  /** ISO; nulo = aberta até alguém encerrar. */
  fecha_em: string | null;
}): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_criar_pesquisa", {
    p_curso_slug: campos.curso,
    p_alvo: campos.alvo,
    p_titulo: campos.titulo,
    p_pergunta: campos.pergunta,
    p_fecha_em: campos.fecha_em,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * O ato de convidar por e-mail (000320). `ensaio` só CONTA — quantos vão receber
 * e por que os outros não; sem ele, enfileira. O e-mail não volta atrás, e por
 * isso a tela sempre ensaia antes e mostra o número na confirmação.
 */
export type ConviteDaPesquisa = Resposta & {
  ensaio?: boolean;
  convidar?: number;
  publico?: number;
  ja_convidados?: number;
  descadastrados?: number;
  sem_email?: number;
  sem_professor?: number;
  ja_responderam?: number;
  enfileirados?: number;
};

export async function convidarPorEmail(pesquisa: string, ensaio: boolean): Promise<ConviteDaPesquisa> {
  const { data, error } = await supabase.rpc("cat_admin_convidar_por_email", { p_pesquisa: pesquisa, p_ensaio: ensaio });
  if (error) return { ok: false, detalhe: error.message };
  return data as ConviteDaPesquisa;
}

export async function encerrarPesquisa(id: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_encerrar_pesquisa", { p_id: id });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * Leva à Central, na hora, as avaliações de professor que ainda não estão lá
 * (000318). A Central já pede isto sozinha a cada 15 minutos; o botão existe
 * para quem acabou de encerrar uma pesquisa. Passa pela porta ÚNICA
 * `professores-da-central` — o hub não fala com a Central por outro caminho.
 */
export async function levarAvaliacoesAgora(): Promise<Resposta & { levadas?: number; com_erro?: number }> {
  const { data, error } = await supabase.functions.invoke("professores-da-central", {
    body: { acao: "levar_avaliacoes" },
  });
  const corpo = (data ?? {}) as Record<string, unknown>;
  if (error) {
    let doCorpo: Record<string, unknown> = corpo;
    // Na resposta não-2xx o SDK guarda o corpo em `error.context`.
    const contexto = (error as { context?: Response }).context;
    if (contexto && typeof contexto.json === "function") {
      try {
        doCorpo = (await contexto.json()) as Record<string, unknown>;
      } catch {
        /* sem corpo legível */
      }
    }
    return {
      ok: false,
      detalhe:
        typeof doCorpo.detalhe === "string"
          ? doCorpo.detalhe
          : typeof doCorpo.erro === "string"
            ? doCorpo.erro
            : error.message,
    };
  }
  const levadas = typeof corpo.levadas === "number" ? corpo.levadas : 0;
  const comErro = typeof corpo.com_erro === "number" ? corpo.com_erro : 0;
  // A frase concorda com o número (a cicatriz do "cerca de 1 páginas").
  const partes: string[] = [];
  if (levadas) partes.push(levadas === 1 ? "1 avaliação levada à Central" : `${levadas} avaliações levadas à Central`);
  if (comErro) {
    partes.push(comErro === 1 ? "1 não chegou, e o motivo fica na pesquisa" : `${comErro} não chegaram, e o motivo fica em cada pesquisa`);
  }
  return {
    ok: true, levadas, com_erro: comErro,
    detalhe: partes.length
      ? `${partes.join("; ")}.`
      : "Nada a levar: todas as avaliações de professor já estão na Central.",
  };
}

export type IndicadoresDoProduto = {
  ok: true;
  medido_em: string;
  matriculas: { total: number; ativas: number };
  turma_atual: number | null;
  professores_da_turma_atual: string[];
  certificados_emitidos: number;
  /** O percentual de conteúdo produzido (000313). Nulo sem aula no controle — e nulo não é 0%. */
  producao: { total: number; produzidas: number; percentual: number | null };
  nps: { pesquisa: string; respostas: number; nps: number | null; aberta: boolean } | null;
  a_definir: Array<{ indicador: string; porque: string }>;
};

export async function lerIndicadoresDoProduto(
  curso: string,
): Promise<IndicadoresDoProduto | (Resposta & { ok: false })> {
  const { data, error } = await supabase.rpc("cat_admin_indicadores_do_produto", { p_curso_slug: curso });
  if (error) return { ok: false, detalhe: error.message };
  return data as IndicadoresDoProduto | (Resposta & { ok: false });
}

/** O NPS escrito como número de NPS: com sinal, sem casa decimal inútil. */
export function npsEmTexto(nps: number | null): string {
  if (nps === null) return "—";
  const inteiro = Number.isInteger(nps) ? String(nps) : nps.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
  return nps > 0 ? `+${inteiro}` : inteiro;
}
